import { z } from 'zod/v4';

import { TarotCardSchema } from '@lilith-arcana-os/domain';

export const CardLibraryEntrySchema = z.object({
  card_id: z.number().int().positive(),
  slug: z.string().min(1),
  summary_short: z.string().min(32),
  summary_long: z.string().min(96),
  upright_expanded: z.string().min(64),
  reversed_expanded: z.string().min(64),
  love_expanded: z.string().min(48),
  work_expanded: z.string().min(48),
  spirit_expanded: z.string().min(48),
  psyche_expanded: z.string().min(48),
  symbolism_expanded: z.string().min(64),
  caution_notes: z.string().min(48),
  journaling_prompts: z.array(z.string().min(16)).min(2),
  ritual_prompts: z.array(z.string().min(16)).min(1),
  study_notes: z.array(z.string().min(16)).min(2),
  theme_tags: z.array(z.string().min(2)).min(1),
  search_tokens: z.array(z.string().min(2)).min(1),
});

export const CardSummarySchema = z.object({
  card_id: z.number().int().positive(),
  slug: z.string().min(1),
  name: z.string().min(1),
  arcana: z.enum(['major', 'minor']),
  suit: z.string().min(1).nullable(),
  element: z.string().min(1),
  summary_short: z.string().min(16),
  symbol_emoji: z.string().min(1),
  keywords: z.array(z.string().min(2)).min(3),
});

export const CardPlaceholderEntrySchema = z.object({
  card_id: z.number().int().positive(),
  gradient_preset: z.string().min(1),
  symbol_emoji: z.string().min(1),
  label: z.string().min(1),
  badge: z.string().min(1),
});

export const AssetPackLicenseSchema = z.enum([
  'public-domain',
  'cc',
  'licensed',
  'custom',
  'placeholder-only',
]);

export const DeckAssetPackSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  description: z.string().min(1),
  enabled_by_default: z.boolean(),
  bundled: z.boolean(),
  license: AssetPackLicenseSchema,
  provenance_note: z.string().min(1),
  card_count: z.number().int().nonnegative(),
  has_thumbnails: z.boolean(),
  has_full_images: z.boolean(),
});

export const CardImageManifestEntrySchema = z.object({
  card_id: z.number().int().positive(),
  asset_pack_id: z.string().min(1),
  image_path: z.string().min(1).nullable(),
  thumb_path: z.string().min(1).nullable(),
  alt_text: z.string().min(1),
  provenance: z.string().min(1),
});

export const CardIndexRefSchema = z.object({
  card_id: z.number().int().positive(),
  slug: z.string().min(1),
});

export const TarotCardArraySchema = z.array(TarotCardSchema);

export interface CardLibraryEntry extends z.infer<typeof CardLibraryEntrySchema> {}
export interface CardSummary extends z.infer<typeof CardSummarySchema> {}
export interface CardPlaceholderEntry extends z.infer<typeof CardPlaceholderEntrySchema> {}
export type AssetPackLicense = z.infer<typeof AssetPackLicenseSchema>;
export interface DeckAssetPack extends z.infer<typeof DeckAssetPackSchema> {}
export interface CardImageManifestEntry extends z.infer<typeof CardImageManifestEntrySchema> {}
export interface CardIndexRef extends z.infer<typeof CardIndexRefSchema> {}
